import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, Building2, Globe } from "lucide-react";
import { cn } from "@/lib/utils";

export default function DioceseSearchSelect({ 
  countries = [], 
  diocesesByCountry = {}, 
  selectedCountry, 
  selectedDiocese,
  onCountryChange,
  onDioceseChange 
}) {
  const [countryOpen, setCountryOpen] = useState(false);
  const [dioceseOpen, setDioceseOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [dioceseSearch, setDioceseSearch] = useState("");

  // Filter countries based on search
  const filteredCountries = countries.filter(country =>
    country.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Get dioceses for selected country and filter
  const dioceses = selectedCountry ? (diocesesByCountry[selectedCountry] || []) : [];
  const filteredDioceses = dioceses.filter(diocese =>
    diocese.toLowerCase().includes(dioceseSearch.toLowerCase())
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Country Selector */}
      <div className="space-y-1.5">
        <label className="text-xs font-['Nunito'] text-[#6B665E]">Country</label>
        <Popover open={countryOpen} onOpenChange={setCountryOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={countryOpen}
              className="w-full justify-between border-[#E5E0D5] font-['Nunito'] text-sm"
              data-testid="diocese-country-search"
            >
              <div className="flex items-center gap-2 truncate">
                <Globe className="w-4 h-4 text-[#6B665E] flex-shrink-0" />
                <span className="truncate">{selectedCountry || "Select country..."}</span>
              </div>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0 bg-white border-[#E5E0D5]" align="start">
            <Command>
              <CommandInput 
                placeholder="Search countries..." 
                value={countrySearch}
                onValueChange={setCountrySearch}
                className="font-['Nunito']"
              />
              <CommandList>
                <CommandEmpty className="py-6 text-center text-sm text-[#6B665E] font-['Nunito']">
                  No country found.
                </CommandEmpty>
                <CommandGroup className="max-h-64 overflow-auto">
                  {filteredCountries.map((country) => (
                    <CommandItem
                      key={country}
                      value={country}
                      onSelect={() => {
                        onCountryChange(country);
                        onDioceseChange(""); // Reset diocese when country changes
                        setCountryOpen(false);
                        setCountrySearch("");
                      }}
                      className="font-['Nunito'] cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedCountry === country ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {country}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Diocese Selector */}
      <div className="space-y-1.5">
        <label className="text-xs font-['Nunito'] text-[#6B665E]">Diocese</label>
        <Popover open={dioceseOpen} onOpenChange={setDioceseOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={dioceseOpen}
              disabled={!selectedCountry}
              className="w-full justify-between border-[#E5E0D5] font-['Nunito'] text-sm disabled:opacity-50"
              data-testid="diocese-search"
            >
              <div className="flex items-center gap-2 truncate">
                <Building2 className="w-4 h-4 text-[#6B665E] flex-shrink-0" />
                <span className="truncate">
                  {selectedDiocese || (selectedCountry ? "Search diocese..." : "Select country first")}
                </span>
              </div>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0 bg-white border-[#E5E0D5]" align="start">
            <Command>
              <CommandInput 
                placeholder="Search dioceses..." 
                value={dioceseSearch}
                onValueChange={setDioceseSearch}
                className="font-['Nunito']"
              />
              <CommandList>
                <CommandEmpty className="py-6 text-center text-sm text-[#6B665E] font-['Nunito']">
                  No diocese found.
                </CommandEmpty>
                <CommandGroup className="max-h-64 overflow-auto">
                  {filteredDioceses.map((diocese) => (
                    <CommandItem
                      key={diocese}
                      value={diocese}
                      onSelect={() => {
                        onDioceseChange(diocese);
                        setDioceseOpen(false);
                        setDioceseSearch("");
                      }}
                      className="font-['Nunito'] cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedDiocese === diocese ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {diocese}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <p className="text-[10px] text-[#6B665E] font-['Nunito'] mt-1">
          {dioceses.length > 0 ? `${dioceses.length} dioceses available` : "Select a country to see dioceses"}
        </p>
      </div>
    </div>
  );
}
